/**
 * API client.
 *
 * Auth is server-managed via an httpOnly session cookie. The browser attaches
 * it automatically because we use `credentials: 'include'`. We do NOT store
 * credentials in JS-accessible storage (no more sessionStorage / Basic Auth).
 *
 * CSRF is handled via a double-submit token. The server sets a readable
 * `csrf_token` cookie; we echo its value back in the `X-CSRF-Token` header
 * on every state-changing request (POST/PUT/PATCH/DELETE).
 */

const CSRF_COOKIE = 'csrf_token';

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Ensure we have a CSRF token. Returns the current value.
 *
 * Safe to call any time — it's a no-op if the cookie is already set, or a
 * single GET otherwise.
 */
export async function getCsrfToken(): Promise<string | null> {
  let token = readCookie(CSRF_COOKIE);
  if (token) return token;
  try {
    await fetch('/api/auth/csrf', { credentials: 'include' });
    token = readCookie(CSRF_COOKIE);
  } catch { /* ignore; apiFetch will still attempt without a token */ }
  return token;
}

/**
 * apiFetch — wraps fetch() with:
 *   - credentials: 'include' so the session cookie is sent and set
 *   - X-CSRF-Token header on state-changing requests
 */
export async function apiFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const method = (options.method || 'GET').toUpperCase();
  const needsCsrf = method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS';

  const headers = new Headers(options.headers || {});
  if (needsCsrf) {
    const token = readCookie(CSRF_COOKIE);
    if (token) headers.set('X-CSRF-Token', token);
  }

  return fetch(url, {
    ...options,
    credentials: 'include',
    headers,
  });
}

// --- Legacy shims (to be removed after full migration) ---------------------
// Some older code paths import these. They are now no-ops — session is
// managed by the server cookie, not by the browser.
export function getCredentials(): string | null { return null; }
export function setCredentials(_encoded: string): void { /* no-op */ }
export function clearCredentials(): void { /* no-op */ }
